import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sessionApi, documentsApi, analysisApi, chatApi } from '@services/sessionApi';
import { useNotify } from '@/contexts/NotificationContext';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useDocumentProgress } from '@/hooks/useDocumentProgress';
import AnalysisResultModal from '@/components/ui/AnalysisResultModal';
import atImage from '/at2.png';

// Analysis tools will be loaded from backend

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'analysis';
  content: string;
  timestamp: Date;
  analysisType?: string;
  analysisTitle?: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Session state - key state to determine if we show landing or active session
  const [session, setSession] = useState<any>(null);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Session functionality states
  const [documents, setDocuments] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analysisTools, setAnalysisTools] = useState<any>({});
  const [analysisProgress, setAnalysisProgress] = useState({ progress: 0, status: 'Başlatılıyor...' });
  const [selectedTool, setSelectedTool] = useState<any>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<any>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);

  // Editable case info states
  const [editingCaseInfo, setEditingCaseInfo] = useState(false);
  const [caseInfo, setCaseInfo] = useState({
    clientName: '',
    counterParty: '',
    casePosition: ''
  });

  // Progress tracking
  const { getDocumentProgress, isProcessing, clearProgress } = useDocumentProgress(sessionKey || '');

  // Load analysis tools on mount
  useEffect(() => {
    loadAnalysisTools();
  }, []);

  // Auto-reload documents when processing completes
  useEffect(() => {
    if (!sessionKey) return;

    const interval = setInterval(() => {
      // Only reload if we have processing or indexing documents
      const processingDocs = documents.filter(doc => doc.status === 'PROCESSING' || doc.status === 'INDEXING');
      if (processingDocs.length > 0) {
        loadDocuments();
      }
    }, 5000); // Check every 5 seconds (reduced frequency to avoid rate limits)

    return () => clearInterval(interval);
  }, [sessionKey, documents]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }, [chatMessages]);

  // Prevent page reload/close when analysis is running
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (runningAnalysis) {
        e.preventDefault();
        e.returnValue = 'Analiz işlemi devam ediyor! Sayfayı kapatırsanız işlem yarıda kalacaktır. Emin misiniz?';
        return e.returnValue;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (runningAnalysis) {
        const confirmLeave = window.confirm('Analiz işlemi devam ediyor! Bu sayfadan ayrılırsanız işlem yarıda kalacaktır. Emin misiniz?');
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Push current state to prevent back button
    if (runningAnalysis) {
      window.history.pushState(null, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [runningAnalysis]);

  // Utility functions for session management
  const loadDocuments = async () => {
    if (!sessionKey) return;
    try {
      const { documents } = await documentsApi.getBySession(sessionKey);
      setDocuments(documents);

      // ✅ Eğer tüm dosyalar silindiyse session'ı sıfırla
      if (documents.length === 0) {
        console.log('📭 Tüm dosyalar silindi, session sıfırlanıyor...');
        setSession(null);
        setSessionKey(null);
        setChatMessages([]);
        navigate('/');
        notify.info('Oturum Sıfırlandı', 'Tüm dosyalar silindi. Yeni strateji başlatabilirsiniz.');
      }
    } catch (error: any) {
      notify.error('Belgeler yüklenemedi', error.message);
    }
  };

  const loadAnalysisTools = async () => {
    try {
      const response = await fetch('/api/analysis/types');
      const tools = await response.json();
      setAnalysisTools(tools);
    } catch (error: any) {
      console.error('Analysis tools yüklenemedi:', error);
    }
  };

  const loadChatHistory = async () => {
    if (!sessionKey) return;
    try {
      const response = await chatApi.getHistory(sessionKey);
      setChatMessages(response.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      })));
    } catch (error: any) {
      console.error('Failed to load chat history:', error);
    }
  };

  // File upload handlers
  const handleFileUpload = async (files: FileList) => {
    if (!sessionKey) return;

    setUploading(true);

    try {
      await documentsApi.upload(sessionKey, files);
      await loadDocuments();
      notify.success('Dosyalar yüklendi', 'Belgeleriniz işlenmeye başlandı.');
    } catch (error: any) {
      notify.error('Yükleme hatası', error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const saveCaseInfo = async () => {
    if (!sessionKey) return;

    try {
      await sessionApi.update(sessionKey, {
        clientName: caseInfo.clientName,
        counterParty: caseInfo.counterParty,
        casePosition: caseInfo.casePosition
      });

      // Update local session
      setSession(prev => ({
        ...prev,
        clientName: caseInfo.clientName,
        counterParty: caseInfo.counterParty,
        casePosition: caseInfo.casePosition
      }));

      setEditingCaseInfo(false);
      notify.success('Güncellendi', 'Taraf bilgileri kaydedildi');
    } catch (error: any) {
      notify.error('Kaydetme Hatası', error.message);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!sessionKey) return;

    // Check if this is the last document
    const readyDocs = documents.filter(doc => doc.status === 'READY');
    if (readyDocs.length === 1 && readyDocs[0].id === documentId) {
      // Show warning modal for last document
      setDocumentToDelete(documentId);
      setShowDeleteWarning(true);
      return;
    }

    // Delete directly if not the last document
    await performDelete(documentId);
  };

  const performDelete = async (documentId: string) => {
    if (!sessionKey) return;

    try {
      await documentsApi.delete(documentId, sessionKey);
      clearProgress(documentId); // Clear progress state to prevent 404 errors
      await loadDocuments();
      notify.success('Belge Silindi', 'Belge başarıyla kaldırıldı');
    } catch (error: any) {
      notify.error('Silme Hatası', error.message);
    }
  };

  const confirmDeleteLast = async () => {
    if (documentToDelete) {
      await performDelete(documentToDelete);
      setShowDeleteWarning(false);
      setDocumentToDelete(null);
    }
  };

  const runAnalysis = async (analysisType: string) => {
    if (!sessionKey) return;

    // Find tool from all categories
    let tool: any = null;
    for (const category of Object.values(analysisTools)) {
      if (Array.isArray(category)) {
        tool = category.find((t: any) => t.type === analysisType);
        if (tool) break;
      }
    }
    if (!tool) return;

    setRunningAnalysis(analysisType);

    // Reset progress
    setAnalysisProgress({ progress: 0, status: 'Başlatılıyor...' });

    // Random interesting legal facts to show while waiting
    const legalFacts = [
      "💡 Delillerin %80'i ilk 72 saat içinde toplanır. Erken aksiyon kritiktir.",
      "⚖️ Mahkeme kararlarının %70'i ilk izlenimlere bağlıdır (Primacy Effect).",
      "📊 İyi organize edilmiş deliller, hakim tarafından %3 kat daha etkili bulunur.",
      "🎯 Karşı tarafın en güçlü argümanını erken ele almak, güvenilirliği %40 artırır.",
      "🧠 İnsanlar mantıktan önce hikayeye inanır - anlatı gücü kanıttan önce gelir.",
      "⏰ Zaman çizelgesi çelişkileri, davaların %60'ında kritik rol oynar.",
      "🔍 Mikro-detaylar makro-resmi çizer. En küçük tutarsızlık domino etkisi yaratır.",
      "💼 Hazırlık, mahkeme başarısının %80'ini oluşturur. İyi strateji her şeyi değiştirir.",
      "📝 Kısa ve net argümanlar, uzun açıklamalardan %2 kat daha etkilidir.",
      "🎭 Mahkeme bir tiyatrodur - sunum tarzı, içerik kadar önemlidir.",
      "🧩 Eksik bilgiler, mevcut kanıtlar kadar önemlidir. 'Olmayan' ne anlatır?",
      "⚡ Çelişkiler sadece zayıflık değil, bazen gizli gerçekleri işaret eder.",
      "🌊 Dava stratejisi statik değil dinamiktir - akışa göre adapte olmalıdır.",
      "💎 Bir güçlü delil, onlarca zayıf delilin yerini tutabilir.",
      "🔗 Görünmez bağlantılar, görünür kanıtlardan daha değerli olabilir."
    ];

    const randomFact = legalFacts[Math.floor(Math.random() * legalFacts.length)];

    // Start progress animation
    const progressSteps = [
      { progress: 15, status: 'Belgeler okunuyor...', delay: 1000 },
      { progress: 35, status: 'AI modeli hazırlanıyor...', delay: 2000 },
      { progress: 55, status: 'Derin analiz yapılıyor...', delay: 3000 },
      { progress: 75, status: 'Sonuçlar derleniyor...', delay: 4000 },
      { progress: 90, status: 'Son kontroller yapılıyor...', delay: 5000 }
    ];

    const progressTimers: NodeJS.Timeout[] = [];
    progressSteps.forEach((step) => {
      const timer = setTimeout(() => {
        setAnalysisProgress({ progress: step.progress, status: step.status });
      }, step.delay);
      progressTimers.push(timer);
    });

    // Add analysis start message ID that we'll use to find and replace
    const startMessageId = `analysis-start-${Date.now()}`;
    const startMessage: ChatMessage = {
      id: startMessageId,
      type: 'analysis',
      content: `PROGRESS_WIDGET:${JSON.stringify({ tool, randomFact })}`,
      timestamp: new Date(),
      analysisType,
      analysisTitle: tool.name
    };

    setChatMessages(prev => [...prev, startMessage]);

    try {
      const readyDocs = documents.filter(doc => doc.status === 'READY');
      const analysis = await analysisApi.generate(sessionKey, analysisType, readyDocs.map(d => d.id));

      // Use analysisInfo from backend if available
      const analysisIcon = analysis.analysisInfo?.icon || tool.icon;
      const analysisName = analysis.analysisInfo?.name || tool.label;

      const resultMessage: ChatMessage = {
        id: `analysis-${analysis.id}`,
        type: 'analysis',
        content: `${analysisIcon} **${analysis.title}**\n\n${analysis.content}`,
        timestamp: new Date(),
        analysisType,
        analysisTitle: analysis.title
      };

      setChatMessages(prev => [...prev.slice(0, -1), resultMessage]); // Replace start message
      notify.success('Analiz Tamamlandı', analysisName + ' analizi hazır');

    } catch (error: any) {
      setChatMessages(prev => prev.slice(0, -1)); // Remove start message

      // Better error message extraction from axios response
      let errorMessage = 'Bilinmeyen bir hata oluştu';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        // If there's additional message, append it
        if (error.response.data.message && error.response.data.message !== errorMessage) {
          errorMessage += ': ' + error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error('Analysis error details:', {
        error,
        responseData: error.response?.data,
        analysisType,
        tool: tool.name
      });

      // Add error message to chat instead of just notification
      const errorMessageObj: ChatMessage = {
        id: `error-${Date.now()}`,
        type: 'assistant',
        content: `❌ **Analiz Hatası: ${tool.name}**\n\n${errorMessage}`,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, errorMessageObj]);
      notify.error('Analiz Hatası', errorMessage);
    } finally {
      setRunningAnalysis(null);
      // Clear progress timers
      progressTimers.forEach(timer => clearTimeout(timer));
    }
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim() || sendingMessage || !sessionKey) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: chatMessage.trim(),
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatMessage('');
    setSendingMessage(true);

    try {
      const response = await chatApi.sendMessage(sessionKey, userMessage.content);

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: response.response,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      notify.error('Mesaj Hatası', error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStrategyStart = async () => {
    setLoading(true);

    try {
      const response = await sessionApi.create({});

      // Set session data locally instead of navigating
      setSessionKey(response.sessionKey);
      setSession(response.session);

      // Initialize case info
      setCaseInfo({
        clientName: response.session?.clientName || '',
        counterParty: response.session?.counterParty || '',
        casePosition: response.session?.casePosition || ''
      });

      // Load session data
      await loadDocuments();
      await loadChatHistory();

      notify.success('Strateji oturumu oluşturuldu!', 'Artık belgelerinizi yükleyip analiz edebilirsiniz.');

    } catch (error: any) {
      notify.error('Oturum oluşturulamadı', error.message || 'Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const isActive = session && sessionKey;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Analysis Running Warning Banner */}
      {runningAnalysis && (
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-3 shadow-lg">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <div className="animate-pulse bg-white bg-opacity-30 rounded-full p-1 mr-3">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-sm">Analiz İşlemi Devam Ediyor</div>
                  <div className="text-xs opacity-90">Bu sayfadan ayrılmayın! İşlem yarıda kalabilir.</div>
                </div>
              </div>
              <div className="ml-6 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1">
        {/* Left Sidebar - Documents & Case Info */}
      <div className="w-80 bg-white shadow-lg border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 border-b border-primary-700">
          <div className="text-center py-3">
            <h1 className="text-xl font-bold text-white mb-1 tracking-tight">Strateji Geliştirici</h1>
            <p className="text-sm text-white font-medium opacity-90">⚖️ Analiz Araçları</p>
          </div>
        </div>

        {/* Activation Button - Show only if not active */}
        {!isActive && (
          <div className="p-6 border-b border-primary-700 bg-gradient-to-r from-primary-500 to-primary-600">
            <div className="mb-4 text-center">
              <p className="text-white text-sm font-medium mb-2">
                📂 Dosya yüklemek, 🤖 AI ile sohbet etmek ve 📊 strateji analizi yapmak için:
              </p>
              <p className="text-secondary-100 text-xs">
                Önce aşağıdaki butona tıklayarak yeni bir strateji oturumu başlatın
              </p>
            </div>
            <button
              onClick={handleStrategyStart}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-bold rounded-lg text-white bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" color="white" className="mr-2" />
                  Başlatılıyor...
                </>
              ) : (
                <>
                  Strateji Başlat
                </>
              )}
            </button>
          </div>
        )}

        {/* Document Upload Section */}
        <div className={`p-6 border-b border-gray-200 relative ${!isActive ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Overlay when not active */}
          {!isActive && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-lg">
              <div className="text-center px-6 py-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border-2 border-primary-300">
                <p className="text-sm font-semibold text-primary-700 mb-1">
                  📂 Dosya yüklemek için
                </p>
                <p className="text-xs text-primary-600">
                  Önce yukarıdaki <span className="font-bold">"Strateji Başlat"</span> butonuna tıklayın
                </p>
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-gray-900">📤 Dosya Yükleme</h3>
                <div className="group relative">
                  <svg className="w-4 h-4 text-gray-400 hover:text-primary-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="invisible group-hover:visible absolute left-0 top-6 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-56 z-10 shadow-lg">
                    Bu oturumdaki belgeler. Yeni dava için "Yeni Strateji" kullanın.
                    <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                  </div>
                </div>
              </div>
              {uploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>}
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-secondary-500 bg-secondary-50' : 'border-primary-200 hover:border-primary-400'
              } ${!isActive ? 'cursor-not-allowed' : ''}`}
              onClick={isActive ? () => document.getElementById('file-input')?.click() : undefined}
              onDrop={isActive ? handleDrop : undefined}
              onDragOver={isActive ? handleDragOver : undefined}
              onDragLeave={isActive ? handleDragLeave : undefined}
            >
              <div className="text-gray-600">
                <p className="text-sm">Dosya Seç veya Sürükle</p>
                <p className="text-xs text-gray-400 mt-1">PDF, DOCX, TXT, MD, UDF (UYAP)</p>
              </div>
            </div>

            <input
              id="file-input"
              type="file"
              multiple
              accept=".pdf,.docx,.txt,.md,.udf"
              className="hidden"
              onChange={(e) => isActive && e.target.files && handleFileUpload(e.target.files)}
              disabled={!isActive}
            />
          </div>

          {/* Documents List */}
          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((doc) => {
                const docProgress = getDocumentProgress(doc.id);
                const isDocProcessing = isProcessing(doc.id);

                return (
                  <div key={doc.id} className="p-2 bg-gray-50 rounded text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-base">
                            {doc.fileType === 'PDF' ? '📄' :
                             doc.fileType === 'DOCX' ? '📝' :
                             doc.fileType === 'UDF' ? '⚖️' :
                             '📃'}
                          </span>
                          <p className="font-medium text-gray-900 truncate">{doc.originalName}</p>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-500 mt-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${
                            doc.status === 'READY' ? 'bg-success-500' :
                            doc.status === 'INDEXING' ? 'bg-yellow-500' :
                            doc.status === 'PROCESSING' ? 'bg-secondary-500' : 'bg-red-500'
                          }`}></span>
                          <span>
                            {doc.status === 'READY' ? 'Hazır' :
                             doc.status === 'INDEXING' ? 'İndeksleniyor' :
                             doc.status === 'PROCESSING' ? 'İşleniyor' : 'Hata'}
                          </span>
                          {docProgress && doc.status !== 'READY' && (
                            <span className="text-primary-500">
                              - {docProgress.message}
                            </span>
                          )}
                        </div>

                        {/* Progress Bar */}
                        {docProgress && isDocProcessing && doc.status !== 'READY' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>{docProgress.progress}%</span>
                              {docProgress.currentPage && docProgress.totalPages && (
                                <span>{docProgress.currentPage}/{docProgress.totalPages} sayfa</span>
                              )}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${docProgress.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}

                        {/* Completion Message */}
                        {doc.status === 'READY' && !isDocProcessing && (
                          <div className="mt-2 text-xs">
                            <div className="flex items-center text-success-500">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium">İşlem tamamlandı - Analiz araçlarını kullanabilirsiniz</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {doc.status === 'READY' && isActive && (
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                          title="Sil"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Case Info */}
        <div className={`flex-1 p-6 overflow-y-auto ${!isActive ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">⚖️ Taraf Bilgileri</h3>
            {isActive && (
              <button
                onClick={() => {
                  if (editingCaseInfo) {
                    saveCaseInfo();
                  } else {
                    setEditingCaseInfo(true);
                  }
                }}
                className="text-xs px-2 py-1 rounded bg-primary-50 text-primary-600 hover:bg-secondary-100 hover:text-secondary-700 transition-colors"
              >
                {editingCaseInfo ? 'Kaydet' : 'Düzenle'}
              </button>
            )}
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Müvekkil Adı</p>
              {editingCaseInfo && isActive ? (
                <input
                  type="text"
                  value={caseInfo.clientName}
                  onChange={(e) => setCaseInfo({ ...caseInfo, clientName: e.target.value })}
                  placeholder="Müvekkil adını giriniz"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              ) : (
                <p className="font-medium text-gray-900">{session?.clientName || 'Belirtilmemiş'}</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 mb-1">Karşı Taraf Adı</p>
              {editingCaseInfo && isActive ? (
                <input
                  type="text"
                  value={caseInfo.counterParty}
                  onChange={(e) => setCaseInfo({ ...caseInfo, counterParty: e.target.value })}
                  placeholder="Karşı taraf adını giriniz"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              ) : (
                <p className="font-medium text-gray-900">{session?.counterParty || 'Belirtilmemiş'}</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 mb-1">Dava Pozisyonumuz</p>
              {editingCaseInfo && isActive ? (
                <select
                  value={caseInfo.casePosition}
                  onChange={(e) => setCaseInfo({ ...caseInfo, casePosition: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="PLAINTIFF">Davacı</option>
                  <option value="DEFENDANT">Davalı</option>
                </select>
              ) : (
                <p className="font-medium text-gray-900">
                  {session?.casePosition === 'PLAINTIFF' ? 'Davacı' :
                   session?.casePosition === 'DEFENDANT' ? 'Davalı' : 'Belirtilmemiş'}
                </p>
              )}
            </div>

            {editingCaseInfo && isActive && (
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={saveCaseInfo}
                  className="flex-1 px-3 py-1 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 transition-colors"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingCaseInfo(false);
                    setCaseInfo({
                      clientName: session?.clientName || '',
                      counterParty: session?.counterParty || '',
                      casePosition: session?.casePosition || ''
                    });
                  }}
                  className="flex-1 px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                >
                  İptal
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col ${!isActive ? 'opacity-50 pointer-events-none' : ''}`}>
        {isActive ? (
          <>
            {/* Chat Messages Area */}
            <div ref={messagesContainerRef} className="h-[750px] overflow-y-auto p-6 bg-gray-50">
              {chatMessages.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  {(() => {
                    const processingCount = documents.filter(doc => doc.status === 'PROCESSING' || doc.status === 'INDEXING').length;
                    const readyCount = documents.filter(doc => doc.status === 'READY').length;
                    const totalDocs = documents.length;

                    if (processingCount > 0) {
                      return (
                        <div className="text-center max-w-md">
                          <div className="text-6xl mb-4">⚙️</div>
                          <p className="text-gray-600 text-lg font-medium mb-2">Belgeler İşleniyor</p>
                          <p className="text-gray-500 text-sm mb-4">
                            {processingCount} belge işleniyor, {readyCount} belge hazır
                          </p>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${totalDocs > 0 ? (readyCount / totalDocs) * 100 : 0}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            Belgeler işlenene kadar lütfen bekleyin
                          </p>
                        </div>
                      );
                    } else if (readyCount > 0) {
                      return (
                        <div className="text-center max-w-md">
                          <div className="text-6xl mb-4">💬</div>
                          <p className="text-gray-600 text-lg font-medium mb-2">AI Asistan Hazır!</p>
                          <p className="text-gray-500 text-sm mb-4">
                            Sağ menüden analiz yapabilir veya aşağıdan soru sorabilirsiniz
                          </p>
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-center max-w-md">
                          <div className="text-6xl mb-4">📁</div>
                          <p className="text-gray-600 text-lg font-medium mb-2">Belge Bekleniyor</p>
                          <p className="text-gray-400 text-sm">
                            Analiz yapmak için lütfen belgelerinizi yükleyin
                          </p>
                        </div>
                      );
                    }
                  })()}
                </div>
              ) : (
                <div className="space-y-4 max-w-4xl mx-auto">
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-3xl rounded-lg px-4 py-3 ${
                          message.type === 'user'
                            ? 'bg-gray-100 text-gray-800 border border-gray-200'
                            : message.type === 'analysis'
                            ? 'bg-white border border-gray-200 shadow-sm'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.type === 'analysis' && (
                          <div className="border-b border-gray-200 pb-2 mb-3">
                            <div className="flex items-center text-sm text-gray-500">
                              <span className="font-medium">Analiz Sonucu</span>
                              <span className="mx-2">•</span>
                              <span>{new Date(message.timestamp).toLocaleTimeString('tr-TR')}</span>
                            </div>
                          </div>
                        )}

                        <div className="prose prose-sm max-w-none">
                          {message.type === 'analysis' && message.content.startsWith('PROGRESS_WIDGET:') ? (
                            // Render progress widget
                            (() => {
                              const data = JSON.parse(message.content.replace('PROGRESS_WIDGET:', ''));
                              const { tool, randomFact } = data;
                              return (
                                <div className="bg-gradient-to-br from-primary-500 to-primary-700 text-white p-5 rounded-xl shadow-xl">
                                  <div className="flex items-center mb-4">
                                    <div className="text-4xl mr-4">{tool.icon}</div>
                                    <div>
                                      <div className="text-xl font-bold">{tool.name}</div>
                                      <div className="text-sm opacity-90">Analiz başlatıldı</div>
                                    </div>
                                  </div>

                                  <div className="bg-white bg-opacity-20 p-3 rounded-lg mb-4 backdrop-blur">
                                    <div className="text-xs opacity-90 mb-1">📋 Ne yapılıyor?</div>
                                    <div className="text-sm leading-relaxed">{tool.description}</div>
                                  </div>

                                  {/* Progress Bar */}
                                  <div className="mb-4">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className="text-xs opacity-90">İlerleme</span>
                                      <span className="text-xs font-bold">{analysisProgress.progress}%</span>
                                    </div>
                                    <div className="bg-white bg-opacity-30 rounded-full h-2.5 overflow-hidden">
                                      <div
                                        className="bg-gradient-to-r from-secondary-400 to-secondary-600 h-full transition-all duration-500 ease-out"
                                        style={{ width: `${analysisProgress.progress}%` }}
                                      ></div>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between bg-white bg-opacity-20 px-4 py-3 rounded-lg backdrop-blur mb-4">
                                    <div className="flex items-center">
                                      <div className="w-2 h-2 bg-secondary-400 rounded-full mr-3 animate-pulse"></div>
                                      <span className="text-sm font-medium">{analysisProgress.status}</span>
                                    </div>
                                    <div className="text-xs opacity-90">⏱️ {tool.estimatedTime}</div>
                                  </div>

                                  {/* Interesting Fact */}
                                  <div className="bg-white bg-opacity-15 border border-white border-opacity-30 p-3 rounded-lg mb-4">
                                    <div className="text-[10px] uppercase tracking-wider opacity-80 mb-1.5">Beklerken...</div>
                                    <div className="text-sm leading-relaxed">{randomFact}</div>
                                  </div>

                                  <div className="bg-red-500 bg-opacity-30 border border-red-400 border-opacity-50 p-3 rounded-lg flex items-start">
                                    <div className="text-lg mr-2.5">⚠️</div>
                                    <div className="text-xs">
                                      <div className="font-semibold mb-1">Bu sayfadan kesinlikle ayrılmayın!</div>
                                      <div className="opacity-95">Analiz işlemi devam ediyor ve sayfayı kapatırsanız işlem yarıda kalacaktır.</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          ) : message.type === 'analysis' && message.content.includes('<div') ? (
                            // Render HTML content for other analysis messages
                            <div dangerouslySetInnerHTML={{ __html: message.content }} />
                          ) : (
                            // Render markdown-style content for normal messages
                            message.content.split('\n').map((line, index) => {
                              // Main title with emoji (starts with emoji)
                              if (line.match(/^[^\w\s]+\s+\*\*(.+)\*\*$/)) {
                                const match = line.match(/^([^\w\s]+)\s+\*\*(.+)\*\*$/);
                                return (
                                  <div key={index} className="bg-white border-l-4 border-blue-500 p-4 rounded-lg mb-4 mt-2 shadow-sm">
                                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                                      <span className="text-3xl mr-3">{match[1]}</span>
                                      <span>{match[2]}</span>
                                    </h2>
                                  </div>
                                );
                              }
                              // Bold headers (**text**)
                              else if (line.startsWith('**') && line.endsWith('**')) {
                                const headerText = line.slice(2, -2);
                                // Check if it's a numbered item header (Delil #1, Argüman #2, etc.)
                                const isNumberedHeader = /^(Delil|Argüman|Mesaj|Bilgi|Kelime|Metin|İfade|Eksik|Bağlantı|Grup|Katman|Soru|Adım|Senaryo|Alternatif|Kritik|Zayıf|Güçlü|Normal)\s+#?\d+/i.test(headerText);

                                return (
                                  <h4 key={index} className={`text-base font-semibold mt-4 mb-2 px-3 py-2 rounded-lg ${
                                    isNumberedHeader
                                      ? 'bg-gray-50 text-gray-800 border-l-4 border-gray-400'
                                      : 'text-gray-900 border-b-2 border-blue-500 pb-2 bg-blue-50'
                                  }`}>
                                    {headerText}
                                  </h4>
                                );
                              }
                              // Markdown headers (## text)
                              else if (line.startsWith('### ')) {
                                return (
                                  <h4 key={index} className="text-lg font-semibold mt-5 mb-2 text-gray-900 flex items-center bg-gray-50 px-4 py-2 rounded-lg border-l-4 border-blue-500">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></span>
                                    {line.slice(4)}
                                  </h4>
                                );
                              }
                              else if (line.startsWith('## ')) {
                                return (
                                  <h4 key={index} className="text-lg font-semibold mt-5 mb-2 text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border-l-4 border-gray-400">
                                    <span className="inline-block mr-2 text-gray-600">▶</span>
                                    {line.slice(3)}
                                  </h4>
                                );
                              }
                              // Bullet points
                              else if (line.startsWith('- ')) {
                                return (
                                  <li key={index} className="ml-6 mb-2 text-gray-700 leading-relaxed list-disc">
                                    {line.slice(2)}
                                  </li>
                                );
                              }
                              // Numbered lists
                              else if (line.match(/^\d+\.\s/)) {
                                return (
                                  <li key={index} className="ml-6 mb-2 text-gray-700 leading-relaxed list-decimal">
                                    {line.replace(/^\d+\.\s/, '')}
                                  </li>
                                );
                              }
                              // Quote blocks (starts with >)
                              else if (line.startsWith('> ')) {
                                return (
                                  <div key={index} className="border-l-4 border-secondary-400 bg-secondary-50 pl-4 py-2 my-2 italic text-gray-700">
                                    {line.slice(2)}
                                  </div>
                                );
                              }
                              // Important notes (starts with ⚠️, 💡, ⚖️, etc)
                              else if (line.match(/^[⚠️💡⚖️📌🔥✅❌]/)) {
                                return (
                                  <div key={index} className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 my-3">
                                    <p className="text-gray-800 font-medium leading-relaxed">{line}</p>
                                  </div>
                                );
                              }
                              // Empty lines
                              else if (line.trim() === '') {
                                return <div key={index} className="h-2"></div>;
                              }
                              // Regular paragraphs
                              else {
                                return (
                                  <p key={index} className="mb-3 text-gray-700 leading-relaxed text-[15px]">
                                    {line}
                                  </p>
                                );
                              }
                            })
                          )}
                        </div>

                        {/* Action buttons for analysis messages */}
                        {message.type === 'analysis' && !message.content.startsWith('PROGRESS_WIDGET:') && (
                          <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => {
                                const blob = new Blob([message.content], { type: 'text/plain;charset=utf-8' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `analiz-${message.analysisType || 'sonuc'}-${new Date().getTime()}.txt`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                              }}
                              className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span>İndir</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAnalysis({
                                  id: message.id,
                                  title: message.analysisTitle || 'Analiz Sonucu',
                                  type: message.analysisType || 'analysis',
                                  content: message.content,
                                  createdAt: message.timestamp
                                });
                                setIsAnalysisModalOpen(true);
                              }}
                              className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                              <span>Detaylı Görünüm</span>
                            </button>
                          </div>
                        )}

                        {message.type === 'user' && (
                          <div className="flex items-center justify-end mt-2 text-xs text-white opacity-70">
                            {new Date(message.timestamp).toLocaleTimeString('tr-TR')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {sendingMessage && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                          <span className="text-gray-600 text-sm">AI düşünüyor...</span>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* Chat Input Area */}
            <div className="border-t border-gray-200 bg-white p-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <textarea
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      placeholder="Belgeleriniz hakkında soru sorun veya analiz isteyin..."
                      className="w-full resize-none rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows={chatMessage.split('\n').length > 1 ? Math.min(chatMessage.split('\n').length, 4) : 1}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendChatMessage();
                        }
                      }}
                      disabled={!isActive || sendingMessage}
                    />
                  </div>
                  <button
                    onClick={sendChatMessage}
                    disabled={!chatMessage.trim() || sendingMessage || !isActive}
                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-secondary-500 text-white hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
                  >
                    {sendingMessage ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>Enter = Gönder, Shift + Enter = Yeni satır</span>
                  <span>{chatMessage.length}/2000</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <img
                  src={atImage}
                  alt="Strateji"
                  className="w-48 h-48 object-contain drop-shadow-2xl"
                />
              </div>
              <p className="text-gray-400 text-lg">Strateji başlatmak için sol taraftaki butona tıklayın</p>
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar - Analysis Tools */}
      <aside className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3">
          <h3 className="text-base font-semibold text-gray-900">🔧 Analiz Araçları</h3>
        </div>

        <div className={`p-4 ${!isActive ? 'opacity-50 pointer-events-none' : ''}`}>
          {Object.entries(analysisTools).map(([category, tools]) => (
            <div key={category} className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2 text-xs uppercase tracking-wide">
                {category === 'core' && '📋 Temel Analizler'}
                {category === 'planning' && '⏰ Planlama Araçları'}
                {category === 'strategy' && '🎯 Strateji Analizleri'}
                {category === 'advanced' && '👥 İleri Analizler'}
                {category === 'specialized' && '🧠 Özel Araçlar'}
              </h4>

              <div className="grid grid-cols-2 gap-1">
                {Array.isArray(tools) && tools.map((tool: any, index: any) => {
                  // Kategoriye göre pastel arka plan renkleri
                  const bgColors: Record<string, string> = {
                    'core': 'bg-primary-50 hover:bg-primary-100 border-primary-200',
                    'planning': 'bg-primary-50 hover:bg-secondary-100 border-primary-200',
                    'strategy': 'bg-secondary-50 hover:bg-secondary-100 border-secondary-200',
                    'advanced': 'bg-primary-100 hover:bg-primary-200 border-primary-300',
                    'specialized': 'bg-secondary-50 hover:bg-secondary-100 border-secondary-300'
                  };

                  // Determine if this is a left or right column button (for tooltip direction)
                  const isLeftColumn = index % 2 === 0;

                  return (
                    <button
                      key={tool.type}
                      onClick={isActive ? () => setSelectedTool(tool) : undefined}
                      disabled={!isActive || runningAnalysis === tool.type || documents.filter(doc => doc.status === 'READY').length === 0}
                      className={`text-left px-2 py-1.5 text-xs border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed group relative ${bgColors[category]} ${tool.name && (tool.name.includes('Karşı Taraf') || tool.name.includes('Değerlendirme') || tool.name.includes('Müzakere')) ? 'col-span-2' : ''}`}
                    >
                    {runningAnalysis === tool.type ? (
                      <div className="flex items-center">
                        <LoadingSpinner size="sm" className="mr-1" />
                        <span className="text-xs">...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-700 group-hover:text-primary-700 truncate">
                            {(() => {
                              const shortNames: Record<string, string> = {
                                '📄 Belge Özeti': '📄 Özet',
                                '⚠️ Sorun Tespiti': '⚠️ Sorunlar',
                                '🔍 Delil Değerlendirmesi': '🔍 Deliller',
                                '⏰ Zaman Çizelgesi': '⏰ Kronoloji',
                                '❓ Kritik Sorular': '❓ Sorular',
                                '🎯 Hukuki Strateji': '🎯 Strateji',
                                '⚡ Çelişki Analizi': '⚡ Çelişkiler',
                                '🗺️ Argüman Haritası': '🗺️ Argümanlar',
                                '👥 Karşı Taraf Analizi': '👥 Karşı Taraf',
                                '📊 Risk Değerlendirmesi': '📊 Riskler',
                                '🤝 Müzakere Stratejisi': '🤝 Müzakere',
                                '🧠 Dava Haritası': '🧠 Dava Haritası',
                                '🎭 Algı Yönetimi Mimarı': '🎭 Algı',
                                '🔗 Görünmez Bağlantı Dedektörü': '🔗 Bağlantılar',
                                '🔍 Anomali Avcısı': '🔍 Anomaliler',
                                '⚛️ Kuantum Olasılık Hesaplayıcı': '⚛️ Olasılıklar',
                                '🏗️ Hukuki Jenga Analizörü': '🏗️ Jenga',
                                '🏺 Arkeolojik Katman Analizörü': '🏺 Katman',
                                '🧩 Eksik Parça Dedektörü': '🧩 Eksikler',
                                '🔬 Moleküler Argüman Sentezleyici': '🔬 Sentez',
                                '🌊 Argüman Akış Analizi': '🌊 Akış',
                                '⚡ Delil Bağlantı Haritası': '⚡ Bağlantı',
                                '🌀 Argüman Sıralama Stratejisi': '🌀 Sıralama',
                                '🎭 Algı Yönetimi Stratejisi': '🎭 Algı',
                                '💎 Delil Ağı Haritası': '💎 Delil Ağı',
                                '🔬 Argüman İyileştirme': '🔬 İyileştirme',
                                '🧠 Duygusal/Mantıksal Denge': '🧠 Denge',
                                '📈 Delil Güç Analizi': '📈 Güç',
                                '📐 Rakam Sunumu Optimizasyonu': '📐 Rakamlar',
                                '🎪 Vurgu Stratejisi': '🎪 Vurgu',
                                '🌊 Bilgi Yükü Optimizasyonu': '🌊 Bilgi Yükü',
                                '🎭 Tekrar Stratejisi': '🎭 Tekrar',
                                '🔤 Metin Doğallık Analizi': '🔤 Doğallık',
                                '📈 İfade Güvenilirlik Analizi': '📈 Güvenilirlik',
                                '🌐 Anahtar Kelime Çıkarıcı': '🌐 Kelimeler'
                              };
                              return shortNames[tool.name] || tool.name;
                            })()}
                          </span>
                        </div>
                      </>
                    )}
                  </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>
      </div>

      {/* Analysis Tool Confirmation Modal */}
      {selectedTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 transform transition-all">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="text-5xl mr-4">{selectedTool.icon}</div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900">{selectedTool.name}</h3>
                <div className="flex items-center mt-1 text-sm text-gray-500">
                  <span className="mr-3">⏱️ {selectedTool.estimatedTime}</span>
                  <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                    {selectedTool.category === 'core' && 'Temel'}
                    {selectedTool.category === 'planning' && 'Planlama'}
                    {selectedTool.category === 'strategy' && 'Strateji'}
                    {selectedTool.category === 'advanced' && 'İleri Seviye'}
                    {selectedTool.category === 'specialized' && 'Özel'}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-4 rounded-lg mb-6 border border-primary-200">
              <div className="text-sm font-semibold text-gray-700 mb-2">📋 Bu analiz ne yapar?</div>
              <p className="text-gray-700 leading-relaxed">
                {selectedTool.description}
              </p>
            </div>

            {/* Warning */}
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 mb-6 flex items-start">
              <div className="text-xl mr-2">⚠️</div>
              <div className="text-xs text-gray-800">
                <div className="font-semibold mb-1">Önemli:</div>
                <div>Analiz başladığında sayfayı kapatmayın veya yenilemeyin. İşlem {selectedTool.estimatedTime} sürebilir.</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedTool(null)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  runAnalysis(selectedTool.type);
                  setSelectedTool(null);
                }}
                disabled={documents.filter(doc => doc.status === 'READY').length === 0}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg font-medium hover:from-secondary-600 hover:to-secondary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
              >
                <span className="mr-2">🚀</span>
                Analizi Başlat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Last Document Warning Modal */}
      {showDeleteWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all">
            {/* Header */}
            <div className="flex items-start mb-4">
              <div className="text-4xl mr-4">⚠️</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Son Belgenizi Siliyorsunuz</h3>
                <p className="text-sm text-gray-600 mt-1">Bu işlem geri alınamaz</p>
              </div>
            </div>

            {/* Content */}
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-800 leading-relaxed mb-3">
                Tüm belgelerinizi silerseniz, bu oturumdaki <strong>tüm analizler kaybolacak</strong> ve oturum otomatik olarak sıfırlanacaktır.
              </p>
              <div className="bg-white border border-primary-200 rounded-lg p-3">
                <div className="flex items-start">
                  <div className="text-2xl mr-2">💡</div>
                  <div className="text-sm">
                    <strong className="text-primary-700">Bilgi:</strong> Son belgeyi sildikten sonra{' '}
                    <strong className="text-secondary-600">"Strateji Başlat"</strong> butonu ile yeni bir oturum başlatabilirsiniz.
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteWarning(false);
                  setDocumentToDelete(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDeleteLast}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Result Modal */}
      {selectedAnalysis && (
        <AnalysisResultModal
          isOpen={isAnalysisModalOpen}
          onClose={() => {
            setIsAnalysisModalOpen(false);
            setSelectedAnalysis(null);
          }}
          analysis={selectedAnalysis}
        />
      )}
    </div>
  );
};

export default HomePage;